import React, { useState, useRef, useEffect, useCallback } from 'react';
import anime from 'animejs';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { isFocusable } from '../../../helpers';
import { breakpoints, weatherIconMap } from '../../../helpers/constants';

const Panel = ({
  isOpen,
  report,
  unit,
  onUnitChange,
  onClose,
  forecastLabel,
  snowLabel,
  acreageLabel,
  snow24hrLabel,
  snow48hrLabel,
  snow7dayLabel
}) => {
  const loaded = useRef(false);
  const panelRef = useRef(null);
  const panelBoxRef = useRef(null);

  const ignoreUtilFocusChanges = useRef(null);
  const lastFocus = useRef(null);
  const navRef = useRef(null);
  const navButtonsRefs = useRef([]);

  const [selectedMountain, setSelectedMountain] = useState(0);
  const { weather, content } = report.mountains[selectedMountain];

  const breakpoint = window.matchMedia(
    `(min-width: ${breakpoints.tabletLandscape}px)`
  );

  const animatePanel = useCallback(
    ({ direction = 'normal' }) => {
      const panel = panelRef.current;
      const panelBox = panelBoxRef.current;

      const timeline = anime.timeline({
        easing: 'easeInOutQuad',
        direction,
        begin: () => {
          if (direction === 'normal') {
            panel.style.visibility = 'visible';
          }
        },
        complete: () => {
          if (direction === 'reverse') {
            panel.style.visibility = 'hidden';
          } else {
            focusFirstDescendant(panelBox);
          }
        }
      });

      timeline.add({
        targets: panel,
        opacity: [0, 1],
        duration: breakpoint.matches ? 150 : 0
      });

      if (breakpoint.matches) {
        timeline.add({
          targets: panelBox,
          translateX: ['100%', '0%'],
          duration: 400
        });

        const animatedElement = panelBox.querySelectorAll('[data-animate]');
        const closeButton = panelBox.querySelector(
          '.weather-widget__panel-close'
        );

        timeline.add({
          targets: [...animatedElement, closeButton],
          opacity: [0, 1],
          translateX: ['10%', '0%'],
          duration: 300,
          delay: anime.stagger(100)
        });
      } else {
        timeline.add({
          targets: panelBox,
          translateY: ['100%', '0%'],
          duration: 400
        });
      }
    },
    [breakpoint.matches, focusFirstDescendant]
  );

  const toggle = useCallback(() => {
    if (loaded.current) {
      if (isOpen) {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${window.scrollY}px`;
        animatePanel({});
      } else {
        const scrollY = document.body.style.top;
        document.body.style.position = null;
        document.body.style.top = null;
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
        animatePanel({ direction: 'reverse' });
      }
    } else {
      loaded.current = true;
    }
  }, [animatePanel, isOpen]);

  useEffect(() => {
    toggle();
  }, [toggle]);

  useEffect(() => {
    const handleFocus = e => {
      if (ignoreUtilFocusChanges.current) return;

      if (panelBoxRef.current.contains(e.target)) {
        lastFocus.current = e.target;
      } else {
        focusFirstDescendant(panelBoxRef.current);

        if (lastFocus.current === document.activeElement) {
          focusLastDescendant(panelBoxRef.current);
        }
        lastFocus.current = document.activeElement;
      }
    };

    document.addEventListener('focus', handleFocus, true);
    return () => {
      document.removeEventListener('focus', handleFocus, true);
    };
  }, [focusFirstDescendant, focusLastDescendant]);

  useEffect(() => {
    const listener = () => {
      panelRef.current.style = null;
      panelBoxRef.current.style = null;
      toggle();
    };

    breakpoint.addListener(listener);
    return () => breakpoint.removeListener(listener);
  }, [breakpoint, toggle]);

  const focusFirstDescendant = useCallback(
    element => {
      for (let i = 0; i < element.childNodes.length; i++) {
        const child = element.childNodes[i];
        if (attemptFocus(child) || focusFirstDescendant(child)) {
          return true;
        }
      }
      return false;
    },
    [attemptFocus]
  );

  const focusLastDescendant = useCallback(
    element => {
      for (let i = element.childNodes.length - 1; i >= 0; i--) {
        const child = element.childNodes[i];
        if (attemptFocus(child) || focusLastDescendant(child)) {
          return true;
        }
      }
      return false;
    },
    [attemptFocus]
  );

  const attemptFocus = useCallback(element => {
    if (!isFocusable(element)) {
      return false;
    }

    ignoreUtilFocusChanges.current = true;
    try {
      element.focus();
    } catch (e) {
      return;
    }
    ignoreUtilFocusChanges.current = false;
    return document.activeElement === element;
  }, []);

  const nf = new Intl.NumberFormat();

  useEffect(() => {
    if (
      navRef.current &&
      navButtonsRefs.current &&
      navButtonsRefs.current[selectedMountain]
    ) {
      const left = navButtonsRefs.current[selectedMountain].offsetLeft;
      navRef.current.scrollTo({
        top: 0,
        left: left - 42,
        behavior: 'smooth'
      });
    }
  }, [selectedMountain]);

  return (
    <div className="weather-widget__panel" ref={panelRef}>
      <div className="weather-widget__panel-box" ref={panelBoxRef}>
        <svg viewBox="0 0 531 600" className="weather-widget__panel-logo">
          <title>aspen logo</title>
          <use xlinkHref="#aspen-leaf" />
        </svg>

        <button
          className="weather-widget__panel-close"
          aria-label="Close"
          onClick={onClose}
        >
          <svg viewBox="0 0 246 246">
            <title>panel close</title>
            <use xlinkHref="#close" />
          </svg>
        </button>
        <ul className="weather-widget__navigation" data-animate ref={navRef}>
          {report.mountains?.map((mountain, index) => (
            <li key={mountain.name}>
              <button
                ref={ref => (navButtonsRefs.current[index] = ref)}
                className={classNames({
                  active: selectedMountain === index
                })}
                onClick={() => setSelectedMountain(index)}
              >
                {mountain.name}
              </button>
            </li>
          ))}
        </ul>
        <div className="weather-widget__panel-inner">
          {!!weather && (
            <div>
              <p className="weather-widget__panel-label" data-animate>
                {forecastLabel}: <strong>{weather.condition_title}</strong>
              </p>
              <div data-animate>
                <div className="weather-widget__panel-unit-toggle">
                  <button
                    className={classNames({ active: unit === 'f' })}
                    onClick={() => onUnitChange('f')}
                  >
                    °F
                  </button>
                  <button
                    className={classNames({ active: unit === 'c' })}
                    onClick={() => onUnitChange('c')}
                  >
                    °C
                  </button>
                </div>
                <div className="weather-widget__panel-today">
                  <p className="weather-widget__panel-value">
                    {weather.temperature.current[unit]}°{unit.toUpperCase()}
                  </p>
                  <svg className="weather-widget__panel-icon">
                    <title>weather</title>
                    <use xlinkHref={`#${weatherIconMap[weather.condition]}`} />
                  </svg>
                  <div className="weather-widget__panel-max-min">
                    <p>
                      <svg viewBox="0 0 11 16">
                        <title>highest</title>
                        <use xlinkHref="#arrow-up" />
                      </svg>
                      {weather.temperature.high[unit]}°{unit.toUpperCase()}
                    </p>
                    <p>
                      <svg viewBox="0 0 11 15">
                        <title>lowest</title>
                        <use xlinkHref="#arrow-down" />
                      </svg>
                      {weather.temperature.low[unit]}°{unit.toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>
              {weather.snow && (
                <>
                  <p className="weather-widget__panel-label" data-animate>
                    {snowLabel}
                  </p>
                  <div className="weather-widget__panel-value-row" data-animate>
                    <p className="weather-widget__panel-value">
                      {weather.snow['_24_hour'].imperial}”
                      <span>{snow24hrLabel}</span>
                    </p>
                    <p className="weather-widget__panel-value">
                      {weather.snow['_48_hour'].imperial}”
                      <span>{snow48hrLabel}</span>
                    </p>
                    <p className="weather-widget__panel-value">
                      {weather.snow['_7_day'].imperial}”
                      <span>{snow7dayLabel}</span>
                    </p>
                  </div>
                </>
              )}
              {!!weather.acres && (
                <div className="weather-widget__panel-value-row">
                  <p className="weather-widget__panel-label" data-animate>
                    {acreageLabel}
                  </p>
                  <p className="weather-widget__panel-value" data-animate>
                    {nf.format(weather.acres.open || 0)}{' '}
                    <span className="weather-widget__panel-value">
                      of {nf.format(weather.acres.total || 0)}
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
          <ul className="weather-widget__links" data-animate>
            {content?.button_links.map(link => (
              <li key={link.url}>
                <a href={link.url} target={link.target}>
                  {link.link_text}
                </a>
              </li>
            ))}
            {content?.text_links.map(link => (
              <li key={link.url}>
                <a href={link.url} target={link.target}>
                  {link.link_text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

Panel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  report: PropTypes.object.isRequired,
  unit: PropTypes.string.isRequired,
  onUnitChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  forecastLabel: PropTypes.string.isRequired,
  snowLabel: PropTypes.string.isRequired,
  acreageLabel: PropTypes.string.isRequired,
  snow24hrLabel: PropTypes.string.isRequired,
  snow48hrLabel: PropTypes.string.isRequired,
  snow7dayLabel: PropTypes.string.isRequired
};

export default Panel;
